import React, { useState } from 'react';

const ROLES = [
  { id:'super_admin', name:'Super Admin', users:1, color:'#A7F432', desc:'Full access to everything including billing, user management, and developer tools.' },
  { id:'admin',       name:'Admin',       users:2, color:'#4fc3f7', desc:'Full CRM access. Can manage users, workflows and most settings. Cannot access billing.' },
  { id:'support',     name:'Support',     users:2, color:'#f5a623', desc:'Can view contacts, deals and manage tickets. Cannot edit workflows or access reports.' },
  { id:'read_only',   name:'Read-only',   users:0, color:'#8a9a97', desc:'Can view all CRM data but cannot make any changes.' },
];

const PERMISSIONS = [
  { section:'Contacts',        actions:['View','Create','Edit','Delete','Export','Bulk actions'] },
  { section:'Deals',           actions:['View','Create','Edit','Delete','Reassign'] },
  { section:'Marketing emails',actions:['View','Create','Send','Delete'] },
  { section:'Workflows',       actions:['View','Create','Edit','Toggle live'] },
  { section:'Reports',         actions:['View','Export'] },
  { section:'Billing',         actions:['View','Edit payment method','Download invoices'] },
  { section:'User management', actions:['View','Invite','Deactivate','Change role'] },
  { section:'Developer',       actions:['View','Create API keys','Manage webhooks'] },
  { section:'Tickets',         actions:['View','Create','Reply','Close','Delete'] },
  { section:'Audit trail',     actions:['View','Export'] },
];

const DEFAULT_PERMS = {
  super_admin: { 'Contacts':['View','Create','Edit','Delete','Export','Bulk actions'], 'Deals':['View','Create','Edit','Delete','Reassign'], 'Marketing emails':['View','Create','Send','Delete'], 'Workflows':['View','Create','Edit','Toggle live'], 'Reports':['View','Export'], 'Billing':['View','Edit payment method','Download invoices'], 'User management':['View','Invite','Deactivate','Change role'], 'Developer':['View','Create API keys','Manage webhooks'], 'Tickets':['View','Create','Reply','Close','Delete'], 'Audit trail':['View','Export'] },
  admin:       { 'Contacts':['View','Create','Edit','Delete','Export','Bulk actions'], 'Deals':['View','Create','Edit','Delete','Reassign'], 'Marketing emails':['View','Create','Send','Delete'], 'Workflows':['View','Create','Edit','Toggle live'], 'Reports':['View','Export'], 'Billing':[], 'User management':['View','Invite','Deactivate'], 'Developer':['View'], 'Tickets':['View','Create','Reply','Close','Delete'], 'Audit trail':['View','Export'] },
  support:     { 'Contacts':['View','Create','Edit'], 'Deals':['View'], 'Marketing emails':['View'], 'Workflows':['View'], 'Reports':[], 'Billing':[], 'User management':[], 'Developer':[], 'Tickets':['View','Create','Reply','Close'], 'Audit trail':['View'] },
  read_only:   { 'Contacts':['View'], 'Deals':['View'], 'Marketing emails':['View'], 'Workflows':['View'], 'Reports':['View'], 'Billing':[], 'User management':[], 'Developer':[], 'Tickets':['View'], 'Audit trail':['View'] },
};

export default function Permissions({ toast }) {
  const [selectedRole, setSelectedRole] = useState('admin');
  const [perms, setPerms] = useState(DEFAULT_PERMS);

  const togglePerm = (section, action) => {
    if (selectedRole==='super_admin') { toast?.('Super Admin permissions cannot be changed','warning'); return; }
    setPerms(prev=>{
      const current = prev[selectedRole][section]||[];
      const next = current.includes(action) ? current.filter(a=>a!==action) : [...current,action];
      return { ...prev, [selectedRole]:{ ...prev[selectedRole],[section]:next } };
    });
  };

  const role = ROLES.find(r=>r.id===selectedRole);
  const rolePerms = perms[selectedRole]||{};

  return (
    <div>
      <div className="stats-grid">
        {ROLES.map(r=>(
          <div key={r.id} className="stat-card" style={{ cursor:'pointer', borderColor:selectedRole===r.id?r.color:undefined }} onClick={()=>setSelectedRole(r.id)}>
            <div className="stat-label">{r.name}</div>
            <div className="stat-value" style={{ color:r.color }}>{r.users}</div>
            <div className="stat-sub">{r.users===1?'user':'users'}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'260px 1fr',gap:20,marginTop:8 }}>
        {/* Role list */}
        <div>
          {ROLES.map(r=>(
            <div key={r.id} onClick={()=>setSelectedRole(r.id)} className="card" style={{ marginBottom:10,cursor:'pointer',borderColor:selectedRole===r.id?r.color:'var(--border)',borderWidth:selectedRole===r.id?2:1 }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6 }}>
                <span style={{ fontWeight:600,fontSize:14,color:selectedRole===r.id?r.color:'var(--text-primary)' }}>{r.name}</span>
                <span style={{ fontSize:11,padding:'2px 8px',borderRadius:99,background:`${r.color}22`,color:r.color }}>{r.users} users</span>
              </div>
              <div style={{ fontSize:12,color:'var(--text-dim)',lineHeight:1.4 }}>{r.desc}</div>
            </div>
          ))}
        </div>

        {/* Permission matrix */}
        <div className="card" style={{ padding:0 }}>
          <div className="card-header" style={{ padding:'16px 20px',borderBottom:'1px solid var(--border)' }}>
            <span className="card-title">Permissions — {role?.name}</span>
            {selectedRole!=='super_admin'&&<button className="btn btn-primary" style={{ fontSize:12 }} onClick={()=>toast?.('Permissions saved')}>Save changes</button>}
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  <th style={{ padding:'12px 16px',textAlign:'left',fontSize:11,color:'var(--text-dim)',fontFamily:'var(--font-ui)',width:160 }}>Section</th>
                  {['View','Create','Edit','Delete','Export','Other actions'].map(a=>(
                    <th key={a} style={{ padding:'12px 8px',textAlign:'center',fontSize:11,color:'var(--text-dim)',fontFamily:'var(--font-ui)' }}>{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSIONS.map(p=>(
                  <tr key={p.section} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'12px 16px',fontWeight:500,fontSize:13,color:'var(--text-primary)' }}>{p.section}</td>
                    {p.actions.slice(0,6).map(action=>{
                      const has = (rolePerms[p.section]||[]).includes(action);
                      const isLocked = selectedRole==='super_admin';
                      return (
                        <td key={action} style={{ padding:'12px 8px',textAlign:'center' }}>
                          <input type="checkbox" checked={has} onChange={()=>togglePerm(p.section,action)} disabled={isLocked}
                            style={{ width:16,height:16,cursor:isLocked?'not-allowed':'pointer',accentColor:'var(--accent)' }} />
                        </td>
                      );
                    })}
                    {p.actions.length<6&&Array(6-p.actions.length).fill(null).map((_,i)=><td key={i}/>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
